import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Button,
  Divider,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Paper,
  CircularProgress,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Notifications as BellIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import Header from '../Components/Header';
import Feed from '../Components/Organizer/Feed';
import Membergrp from '../Components/Organizer/Membergrp';
import Photo from '../Components/Organizer/Photo';
import Sendinvites from '../Components/Organizer/Sendinvites';
import Manage from '../Components/Organizer/Manage';

const GOLD = "#D4AF37";

const getGroupCoverPhoto = (groupId) => {
  const coverPhotos = [
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1521791136064-7986c2920216?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80",
    "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
  ];
  return coverPhotos[groupId % coverPhotos.length];
};

const getGroupAvatar = (groupId) => {
  const avatars = [
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
    "https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80",
    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-1.2.1&auto=format&fit=crop&w=200&q=80"
  ];
  return avatars[groupId % avatars.length];
};

const CreateGroupPage2 = () => {
  const { id } = useParams();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('jwttoken');
  const backendURL = 'https://tdtlworld.com/dss-kaas-backend';

  const tabs = ['Members', 'Feed', 'Photo', 'Send Invites', 'Manage'];

  useEffect(() => {
    const fetchGroupDetails = async () => {
      if (!id) return;
      try {
        const response = await axios.get(`${backendURL}/socialapp/groups/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGroupData(response.data);
      } catch (error) {
        console.error('Error fetching group data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGroupDetails();
  }, [id, token]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    switch (tabs[activeTab]) {
      case 'Members': return <Membergrp members={groupData?.members || []} />;
      case 'Feed': return <Feed />;
      case 'Photo': return <Photo />;
      case 'Send Invites': return <Sendinvites />;
      case 'Manage': return <Manage />;
      default: return <Membergrp members={groupData?.members || []} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: GOLD }} />
        <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Loading Group...
        </Typography>
      </Box>
    );
  }

  if (!groupData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="error">
          Failed to load group data. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header />
      <Container maxWidth="lg" sx={{ mt: '88px', pb: 6 }}>
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: '24px', 
            overflow: 'hidden', 
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(GOLD, 0.1)}`,
            boxShadow: theme.palette.mode === 'dark' ? '0 20px 50px rgba(0,0,0,0.5)' : '0 20px 50px rgba(0,0,0,0.05)'
          }}
        >
          {/* Cover Section */}
          <Box 
            sx={{ 
              height: { xs: 200, md: 320 }, 
              position: 'relative',
              backgroundImage: `url(${getGroupCoverPhoto(groupData.id)})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute', 
                inset: 0, 
                background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.7) 100%)' 
              }} 
            />
            <Tooltip title="Change Cover Photo">
              <IconButton 
                sx={{ 
                  position: 'absolute', 
                  top: 20, 
                  right: 20, 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                }}
              >
                <CameraIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Header Content Section */}
          <Box sx={{ px: { xs: 3, md: 4 }, pb: 3, position: 'relative' }}>
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' }, 
                alignItems: { xs: 'center', md: 'flex-end' },
                gap: 3,
                mt: { xs: -8, md: -10 }
              }}
            >
              {/* Avatar */}
              <Avatar
                src={getGroupAvatar(groupData.id)}
                sx={{ 
                  width: { xs: 120, md: 160 }, 
                  height: { xs: 120, md: 160 }, 
                  border: `6px solid ${theme.palette.background.paper}`,
                  boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                  borderRadius: '30px'
                }}
              />

              {/* Group Info */}
              <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' }, mb: { md: 2 } }}>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    fontWeight: 800, 
                    mb: 1, 
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    fontFamily: "'Outfit', sans-serif"
                  }}
                >
                  {groupData.name}
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      px: 2, py: 0.5, 
                      borderRadius: '50px', 
                      bgcolor: alpha(GOLD, 0.1), 
                      color: GOLD,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <PublicIcon fontSize="small" />
                    {groupData.privacy} Group
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CalendarIcon fontSize="inherit" />
                    Active 6 hours ago
                  </Typography>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="inherit" />
                    Organizer: <strong>{groupData.created_by?.first_name} {groupData.created_by?.last_name}</strong>
                  </Typography>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: { md: 2 } }}>
                <Button 
                  variant="contained" 
                  startIcon={<PersonIcon />}
                  sx={{
                    borderRadius: '12px',
                    px: 3,
                    bgcolor: GOLD,
                    '&:hover': { bgcolor: '#B8860B' }
                  }}
                >
                  Organizer
                </Button>
                <IconButton 
                  sx={{ 
                    bgcolor: alpha(theme.palette.text.primary, 0.05),
                    borderRadius: '12px',
                    p: 1.5
                  }}
                >
                  <BellIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Navigation Tabs */}
            <Box sx={{ mt: 4, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 4,
                    borderRadius: '4px 4px 0 0',
                    bgcolor: GOLD
                  },
                  '& .MuiTab-root': {
                    minHeight: 64,
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: GOLD
                    }
                  }
                }}
              >
                {tabs.map((label, index) => (
                  <Tab key={index} label={label} />
                ))}
              </Tabs>
            </Box>
          </Box>
        </Paper>

        {/* Tab Content Area */}
        <Box sx={{ mt: 4 }}>
          {renderTabContent()}
        </Box>
      </Container>
    </Box>
  );
};

export default CreateGroupPage2;
